import React from 'react';
import { Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../hooks/use-toast';

/**
 * Returns whether the current tenant can perform write operations.
 * Frozen / cancelled tenants are read-only (post-trial expiry lock).
 */
export const useTenantWritable = () => {
  const { currentBusiness } = useBusiness();
  const status = currentBusiness?.subscription_status;
  const isLocked = status === 'frozen' || status === 'cancelled';
  return {
    writable: !isLocked,
    locked: isLocked,
    status,
  };
};

/**
 * <FeatureGate> wraps any "create" / "write" action. When the tenant is in a
 * read-only state, it visually dims the children, intercepts clicks, and shows
 * a toast pointing the user to the Reactivate flow on /business/billing.
 *
 * Usage:
 *   <FeatureGate>
 *     <Button>New Conversation</Button>
 *   </FeatureGate>
 */
export const FeatureGate = ({ children, label = 'this feature', className = '', ...rest }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const { locked } = useTenantWritable();

  if (!locked) {
    // Forward props injected by a parent such as <DialogTrigger asChild> (onClick,
    // aria-*, data-state) to the wrapped element. Without this the trigger's click
    // never reaches the child Button and the dialog/menu never opens.
    return (React.isValidElement(children) && rest && Object.keys(rest).length > 0)
      ? React.cloneElement(children, rest)
      : children;
  }

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toast({
      title: t('dash.readOnly.title'),
      description: t('dash.readOnly.desc', { label }),
      variant: 'destructive',
    });
    navigate('/business/billing');
  };

  return (
    <span
      onClickCapture={handleClick}
      data-testid="feature-gate-locked"
      className={`relative inline-block opacity-60 cursor-not-allowed ${className}`}
      title={t('dash.readOnly.tooltip')}
    >
      <span className="pointer-events-none">{children}</span>
      <Lock className="absolute -top-1 -right-1 h-3 w-3 text-gray-700 bg-white rounded-full p-0.5 shadow" />
    </span>
  );
};

export default FeatureGate;